import React, { memo, useEffect, useState, useCallback } from 'react';
import { Formik, Field } from 'formik';
import { useReadLocalStorage } from 'usehooks-ts';
import { uniqWith, isEqual, debounce } from 'lodash';

// Types
import { SearchType } from './types';
import { DepartureOption } from '@lt/api/dist/types/departure';
import { SearchLand } from '@lt/api/dist/methods/search/types';
import UserflowTheme from '@lt/components/themes/userflow';
// Styles:
import { StyledForm } from './styles';

// Context:
import { CigaretteProvider, PreviousSearchesOption, RequiredValues, Values } from './context';

// Constants:
import {
  INITIAL_VALUES_FORMIK,
  SEARCH_TYPE_NAMES,
  FIELD_NAMES,
  LEFT_BORDER_CLASS,
  storageKeys,
  LAND_PARAMS_COUNT,
} from './constants';

// Components:
import FormDeparturePicker from './components/FormDeparturePicker';
import FormDatePicker from './components/FormDatePicker';
import FormDestinationPicker from './components/FormDestinationPicker';
import FormTouristsPicker from './components/FormTouristsPicker';
import BackWizardButton from './components/BackWizardButton';
import CloseWizardButton from './components/CloseWizardButton';
import SearchButton from './components/SearchButton';
import { useFetchDepartures, useAutocomplete, useSearchLand } from './hooks';
import { buildLandParams } from './utils/buildLandParams';
import { validate } from './validate';
import { getSearchParamsChanges, isEqualPreviousSearches } from './utils';
import { withApiContext } from '../../context/hocs/withApiContext';

type CigaretteProps = {
  searchType: SearchType;
  mobileVariant?: 'default' | 'wizard';
  initialValues?: Values;
  onSubmit?: (values: SearchLand) => void;
  host?: string;
  isMainPage?: boolean;
  hasHotelSearch?: boolean;
  className?: string;
  setIsWizardOpen?: (newValue: boolean) => void;
  variant?: 'default' | 'hotel';
  openNewTab?: boolean;
  onChange?: (values: Values, changes: Partial<Values>) => void;
  isGeoPositionDisabled?: boolean;
};

const BaseCigarette = ({
  searchType = 'package',
  mobileVariant,
  initialValues = INITIAL_VALUES_FORMIK,
  host = window.location.origin,
  onSubmit,
  isMainPage,
  hasHotelSearch,
  className = '',
  setIsWizardOpen,
  variant = 'default',
  onChange,
  isGeoPositionDisabled = false,
}: CigaretteProps) => {
  const isWizard = mobileVariant === 'wizard';
  const isMobile = !!mobileVariant;

  const {
    departures,
    loading: departuresLoading,
    popupCityName,
    setPopupCityName,
  } = useFetchDepartures(isGeoPositionDisabled);
  const {
    startOptions,
    fullOptions,
    foundGroupedOptions,
    setParams,
    setDepartureValue,
    savedLandParams,
    setSavedLandParams,
  } = useAutocomplete(searchType, isWizard);

  const { setParams: setSubmitParams, loading: landLoading } = useSearchLand({});
  const savedDeparture = useReadLocalStorage<DepartureOption | null>(storageKeys.departure);

  const submitHandler = (values: Values) => {
    // сабмит сработает только после валидации, когда все поля будут заполнены
    const landParams = buildLandParams({
      ...(values as RequiredValues),
      host,
      searchType,
    });

    const lastPreviousSearch: PreviousSearchesOption = {
      ...(values as RequiredValues),
      searchType,
      groupName: 'previousSearches',
      value: values.destination?.value,
    };
    // из локалстораджа прилетают строки вместо дат, делаем конвертацию через прогон методов JSON для корректного сравнения ниже
    const lastPreviousSearchWithConvertDate = JSON.parse(JSON.stringify(lastPreviousSearch));
    const newSavedLandParams = [
      lastPreviousSearchWithConvertDate,
      ...(savedLandParams[searchType] || []),
    ];
    const uniqNewSavedLandParamsByType = uniqWith(newSavedLandParams, isEqualPreviousSearches);

    const uniqNewSavedLandParams = {
      ...savedLandParams,
      [searchType]: uniqNewSavedLandParamsByType.slice(0, LAND_PARAMS_COUNT),
    };
    setSavedLandParams(uniqNewSavedLandParams);
    if (onSubmit) {
      onSubmit(landParams);
    } else {
      setSubmitParams(landParams);
    }
  };

  return (
    // Решает конфликты при интеграции на сайт вместе с друой версией lt-components
    <UserflowTheme>
      <CigaretteProvider isMobile={isMobile} isWizard={isWizard} setIsWizardOpen={setIsWizardOpen}>
        <Formik
          enableReinitialize={variant === 'hotel'}
          initialValues={{
            ...initialValues,
            departure: initialValues.departure || savedDeparture || undefined,
          }}
          validateOnChange={false}
          validateOnBlur={false}
          onSubmit={submitHandler}
          validate={isMobile ? undefined : (values) => validate(values, searchType)}
        >
          {({ values, initialValues, isValid, ...props }) => (
            <>
              {onChange ? (
                <CigaretteChangeListener
                  values={values}
                  initialValues={initialValues}
                  isValid={isValid}
                  onChange={onChange}
                />
              ) : null}
              <StyledForm
                className={className}
                $isMobile={isMobile}
                $isWizardOpen={isWizard && !!values.destination}
                $isWizard={isWizard}
                $hasHotelSearch={hasHotelSearch}
                $variant={variant}
                $searchType={searchType}
              >
                <Field
                  id={FIELD_NAMES.destination}
                  name={FIELD_NAMES.destination}
                  fullOptions={fullOptions}
                  foundGroupedOptions={foundGroupedOptions}
                  startOptions={startOptions}
                  fetchAutocomplete={setParams}
                  fetchPlaces={setDepartureValue}
                  className={LEFT_BORDER_CLASS}
                  searchType={searchType}
                  variant={variant}
                  as={FormDestinationPicker}
                />
                <Field
                  id={FIELD_NAMES.datepicker}
                  name={FIELD_NAMES.datepicker}
                  className={LEFT_BORDER_CLASS}
                  as={FormDatePicker}
                  searchType={searchType}
                  variant={variant}
                />
                <Field
                  id={FIELD_NAMES.tourists}
                  name={FIELD_NAMES.tourists}
                  className={LEFT_BORDER_CLASS}
                  as={FormTouristsPicker}
                  variant={variant}
                />
                {searchType === SEARCH_TYPE_NAMES.PACKAGE && (
                  <Field
                    id={FIELD_NAMES.departure}
                    name={FIELD_NAMES.departure}
                    value={departures}
                    className={LEFT_BORDER_CLASS}
                    isLoading={departuresLoading}
                    popupCityName={popupCityName}
                    setPopupCityName={setPopupCityName}
                    isMainPage={isMainPage}
                    variant={variant}
                    as={FormDeparturePicker}
                  />
                )}
                <BackWizardButton />
                <CloseWizardButton searchType={searchType} />
                <SearchButton loading={landLoading} />
              </StyledForm>
            </>
          )}
        </Formik>
      </CigaretteProvider>
    </UserflowTheme>
  );
};

// https://github.com/jaredpalmer/formik/issues/2769#issuecomment-845235005
type ICigaretteChangeListener = {
  values: Values;
  initialValues: Values;
  isValid: boolean;
  onChange: (values: Values, changes: Partial<Values>) => void;
};

const CigaretteChangeListener = (props: ICigaretteChangeListener) => {
  const { values, initialValues, isValid, onChange } = props;
  const [lastValues, updateState] = useState(values);

  const handleChange = useCallback(
    debounce(
      (values, changes): void => {
        onChange(values, changes);
      },
      500,
      { maxWait: 1500 }
    ),
    []
  );

  useEffect(() => {
    const valuesEqualLastValues = isEqual(lastValues, values);
    const valuesEqualInitialValues = values === initialValues;

    if (!valuesEqualLastValues && !valuesEqualInitialValues && isValid) {
      const searchParamsChanges = getSearchParamsChanges(lastValues, values);
      handleChange(values, searchParamsChanges);
    }

    if (!valuesEqualLastValues) {
      updateState(values);
    }
  }, [values, isValid]);

  return null;
};

/** для использования как компонента на сайте */
export const Cigarette = withApiContext<CigaretteProps>(memo(BaseCigarette));

/** для использования как виджета */
export default memo(BaseCigarette);
